import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { isAnalyticsSection } from 'components/DisasterGPT/constants/opordSectionDefaults';

export const ANALYTICS_LAYER_SECTION_MARK = '::analytics-layer::';

type LayerRef = {
  id?: string;
  kind?: string;
  name?: string;
};

type LayersSource = {
  source?: string;
  selectedLayers?: LayerRef[];
};

export function isVirtualAnalyticsLayerSection(section: {
  id?: string | null;
}): boolean {
  return (section.id ?? '').includes(ANALYTICS_LAYER_SECTION_MARK);
}

export function selectedLayersOnSection(
  section:
    | {
        data_sources?: unknown;
        data_source?: unknown;
      }
    | null
    | undefined
): LayerRef[] {
  const sources = Array.isArray(section?.data_sources)
    ? (section.data_sources as LayersSource[])
    : Array.isArray(section?.data_source)
      ? (section.data_source as LayersSource[])
      : [];
  const entry = sources.find((source) => source?.source === 'Layers');
  return Array.isArray(entry?.selectedLayers) ? entry.selectedLayers : [];
}

function layerKey(layer: LayerRef): string {
  return `${layer.kind ?? 'external'}:${layer.id ?? layer.name ?? ''}`;
}

/** Keep layer-stats JSON (and a saved geo snapshot) for one selected layer. */
export function filterLayerStatsDataString(
  raw: string | null | undefined,
  layers: LayerRef[]
): string | null {
  if (!raw || layers.length !== 1 || !layers[0]?.id) return raw ?? null;
  const layerId = layers[0].id;
  const layerName = layers[0].name;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return raw;
    const next = { ...parsed };
    const results = parsed.LayerQueryResults as
      | { layers?: Array<{ id?: string; name?: string }> }
      | undefined;
    if (results && Array.isArray(results.layers)) {
      const matched = results.layers.filter((layer) => layer?.id === layerId);
      if (matched.length > 0 && matched.length !== results.layers.length) {
        next.LayerQueryResults = { ...results, layers: matched };
      }
    }
    const geo = parsed.geoSnapshot as
      | { layers?: Array<{ id?: string; name?: string }> }
      | undefined;
    if (geo && Array.isArray(geo.layers)) {
      const matched = geo.layers.filter((layer) => {
        const id = layer?.id ?? '';
        return (
          id === layerId ||
          id.startsWith(`${layerId}:`) ||
          (layerName != null && layer?.name === layerName)
        );
      });
      if (matched.length > 0 && matched.length !== geo.layers.length) {
        next.geoSnapshot = { ...geo, layers: matched };
      }
    }
    return JSON.stringify(next);
  } catch {
    return raw;
  }
}

function virtualLayerSection(
  parent: SitrepSectionType,
  layer: LayerRef,
  index: number
): SitrepSectionType {
  const name = layer.name?.trim() || 'Layer';
  const versions = (parent.section_versions ?? []).map((version) => ({
    ...version,
    data_string: filterLayerStatsDataString(version.data_string, [layer]),
  }));
  return {
    ...parent,
    id: `${parent.id}${ANALYTICS_LAYER_SECTION_MARK}${layerKey(layer)}`,
    name,
    parent_section_id: parent.id,
    previewSectionId: parent.id,
    order: (parent.order ?? 0) + (index + 1) / 100,
    data_sources: [
      {
        source: 'Layers',
        selectedLayers: [layer],
      },
    ] as unknown as SitrepSectionType['data_sources'],
    section_versions: versions,
  };
}

/**
 * Approved reports keep one stored Analytics section. Show each saved layer as
 * a nested section without rewriting that snapshot. Drafts that already have
 * real children are left alone.
 */
export function expandAnalyticsLayerSections(
  sections: SitrepSectionType[]
): SitrepSectionType[] {
  if (sections.length === 0) return sections;
  const childIds = new Set(
    sections
      .map((section) => section.parent_section_id)
      .filter((id): id is string => !!id)
  );
  const expanded: SitrepSectionType[] = [];
  for (const section of sections) {
    expanded.push(section);
    if (section.parent_section_id || !isAnalyticsSection(section)) continue;
    if (childIds.has(section.id)) continue;
    const layers = selectedLayersOnSection(section).filter(
      (layer) => layer?.id
    );
    layers.forEach((layer, index) => {
      expanded.push(virtualLayerSection(section, layer, index));
    });
  }
  return expanded;
}
